import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, UserPlus, Send, ChevronDown, Ban, Play, Trash2, Shield,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLE_META = {
  super_user: { label: "Super User", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  user: { label: "User", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STATUS_CLS = {
  active: "bg-green-100 text-green-800 border-green-200",
  invited: "bg-amber-100 text-amber-800 border-amber-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
};

function fmtDateTime(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function TeamPanel() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState({ name: "", email: "", role: "user" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setTeam(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) {
      toast.error("Please add a name and email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/team/invite", invite);
      toast.success(`Invitation emailed to ${invite.email}`);
      setInvite({ name: "", email: "", role: "user" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not invite user");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, role) => {
    if (u.role === role) return;
    try {
      await api.patch(`/team/users/${u.id}`, { role });
      toast.success(`${u.name || u.email} is now ${ROLE_META[role].label}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change role");
    }
  };

  const toggleSuspend = async (u) => {
    const next = u.status === "suspended" ? "active" : "suspended";
    try {
      await api.patch(`/team/users/${u.id}`, { status: next });
      toast.success(next === "suspended" ? "Access suspended" : "Access restored");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update user");
    }
  };

  const resend = async (u) => {
    try {
      await api.post(`/team/users/${u.id}/resend-invite`);
      toast.success(`Invitation resent to ${u.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend invite");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from your team? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/users/${u.id}`);
      toast.success("Team member removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove user");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="team-panel">
      <h2 className="font-display text-xl font-bold text-slate-900 mb-1">Your Team</h2>
      <p className="text-sm text-slate-500 mb-5">Invite people to help manage your website. Super Users can edit; Users have read-only access.</p>

      {/* Invite */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Invite a team member</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={invite.name} onChange={(e) => setInvite((v) => ({ ...v, name: e.target.value }))} data-testid="team-invite-name" placeholder="Name" />
          <Input type="email" value={invite.email} onChange={(e) => setInvite((v) => ({ ...v, email: e.target.value }))} data-testid="team-invite-email" placeholder="email@church.org" />
        </div>
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Role</Label>
          <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="team-invite-role-picker">
            {[
              { id: "super_user", label: "Super User", desc: "Can edit the website" },
              { id: "user", label: "User", desc: "Read-only access" },
            ].map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`team-invite-role-${r.id}`}
                onClick={() => setInvite((v) => ({ ...v, role: r.id }))}
                className={`rounded-lg border p-2.5 text-left transition-colors duration-200 ${invite.role === r.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                <div className="text-xs text-slate-500">{r.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <Button onClick={sendInvite} disabled={busy} data-testid="team-invite-btn" className="mt-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white">
          <Send className="h-4 w-4 mr-2" /> Send Invite
        </Button>
      </div>

      {/* Members */}
      {loading ? (
        <div className="py-10 grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" /></div>
      ) : (
        <div className="space-y-2" data-testid="team-members-list">
          {team.length === 0 && <p className="text-sm text-slate-400">No team members yet.</p>}
          {team.map((u) => (
            <div key={u.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`team-user-row-${u.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{u.name || u.email}</span>
                    {u.role === "super_user" && <Shield className="h-3.5 w-3.5 text-amber-600" />}
                    <Badge variant="outline" className={`${ROLE_META[u.role]?.cls || ROLE_META.user.cls} border text-xs`} data-testid={`team-role-badge-${u.id}`}>{ROLE_META[u.role]?.label || "User"}</Badge>
                    <Badge variant="outline" className={`${STATUS_CLS[u.status] || STATUS_CLS.active} border text-xs`}>{u.status}</Badge>
                    {u.is_self && <span className="text-xs text-slate-400">(you)</span>}
                  </div>
                  <div className="text-sm text-slate-500">{u.email}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Last login: {fmtDateTime(u.last_login)}</div>
                </div>
                {!u.is_self && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`team-actions-${u.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                        Manage <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger data-testid={`team-role-submenu-${u.id}`}>Change role</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {["super_user", "user"].map((r) => (
                            <DropdownMenuItem key={r} data-testid={`team-role-${r}-${u.id}`} onClick={() => changeRole(u, r)}>
                              {ROLE_META[r].label}{u.role === r ? " ✓" : ""}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => toggleSuspend(u)} data-testid={`team-suspend-${u.id}`}>
                        {u.status === "suspended" ? <><Play className="h-4 w-4 mr-2" />Restore access</> : <><Ban className="h-4 w-4 mr-2" />Suspend access</>}
                      </DropdownMenuItem>
                      {u.status === "invited" && (
                        <DropdownMenuItem onClick={() => resend(u)} data-testid={`team-resend-${u.id}`}>
                          <Send className="h-4 w-4 mr-2" />Resend invite
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => remove(u)} data-testid={`team-delete-${u.id}`} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" />Remove member
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
